import { createContext, useCallback, useContext, type ReactNode } from 'react';

/** Answers a catalogue key in the viewer's language, or `undefined` when the catalogue has no entry for it. */
export type DsTranslator = (key: string, params?: Record<string, unknown>) => string | undefined;

/** `t(key, english, params)`: the viewer's text for `key`, else the English default with `{name}` filled in. */
export type DsT = (key: string, english: string, params?: Record<string, unknown>) => string;

const DsI18nContext = createContext<DsTranslator | null>(null);

export interface DsI18nProviderProps {
  translate: DsTranslator;
  children?: ReactNode;
}

/**
 * C3: the design system's own words (grid chrome, filters, saved views, pagers, pickers, empty and error states)
 * in the viewer's language. The app passes its catalogue lookup once; without a provider every component keeps
 * speaking the English it was written with, so the package stays usable on its own.
 */
export function DsI18nProvider({ translate, children }: DsI18nProviderProps) {
  return <DsI18nContext.Provider value={translate}>{children}</DsI18nContext.Provider>;
}

/** Fills `{name}` placeholders; plural and select forms belong in the catalogue, not in the English default. */
export function fillDsParams(text: string, params?: Record<string, unknown>): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in params && params[name] != null ? String(params[name]) : whole));
}

/** The raw translator in scope (or null) — for handing it to a subtree rendered above the provider (toasts). */
export function useDsTranslator(): DsTranslator | null {
  return useContext(DsI18nContext);
}

/** The design-system translator in scope; English defaults when no provider is mounted. */
export function useDsT(): DsT {
  const translate = useContext(DsI18nContext);
  return useCallback<DsT>((key, english, params) => translate?.(key, params) ?? fillDsParams(english, params), [translate]);
}
